import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowLeft, TrendingUp, AlertCircle, Award } from "lucide-react";
import {
  calculateWHR,
  getWHRCategory,
  getVisceralFatZone,
  calculateCompositionScore,
  getLastNDaysRecords,
} from "./utils/healthCalculations";

const HealthScores = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [scores, setScores] = useState({
    whr: null,
    visceralFat: null,
    composition: null,
  });

  useEffect(() => {
    if (user?.dailyRecords?.records) {
      const last30Days = getLastNDaysRecords(user.dailyRecords.records, 30);
      if (last30Days.length > 0) {
        const latestRecord = last30Days[last30Days.length - 1];
        calculateScores(latestRecord);
      }
    }
  }, [user]);

  const calculateScores = (record) => {
    // Calculate WHR
    const whr = calculateWHR(record.waistLine, record.hip);
    const whrCategory = getWHRCategory(whr, user?.personalDetails?.gender);

    // Get Visceral Fat Zone
    const visceralZone = getVisceralFatZone(record.visceralFat);

    // Calculate Composition Score
    const compositionScore = calculateCompositionScore(
      record.fat,
      record.muscles,
      record.bmi,
      whr
    );

    setScores({
      whr: { value: whr, category: whrCategory, record },
      visceralFat: { value: record.visceralFat, zone: visceralZone },
      composition: { score: compositionScore, record },
    });
  };

  const ScoreCard = ({ title, icon: Icon, children, color = "blue" }) => {
    const colorClasses = {
      blue: "from-blue-50 to-blue-100 border-blue-200",
      green: "from-green-50 to-green-100 border-green-200",
      yellow: "from-yellow-50 to-yellow-100 border-yellow-200",
      red: "from-red-50 to-red-100 border-red-200",
      purple: "from-purple-50 to-purple-100 border-purple-200",
    };

    return (
      <div
        className={`bg-gradient-to-br ${colorClasses[color]} rounded-lg p-5 border`}
      >
        <div className="flex items-center gap-2 mb-4">
          <Icon className="w-5 h-5" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        {children}
      </div>
    );
  };

  return (
    <div className="p-5 space-y-5">
      {!scores.composition ? (
        <div className="text-center py-12">
          <p className="text-gray-500">
            No health data available to calculate scores
          </p>
        </div>
      ) : (
        <>
          {/* Composition Score */}
          <ScoreCard title="Composition Score" icon={Award} color="purple">
            <div className="mb-4">
              <div className="flex items-end gap-2 mb-2">
                <span className="text-5xl font-bold text-purple-900">
                  {scores.composition.score}
                </span>
                <span className="text-2xl text-purple-600 mb-2">/100</span>
              </div>
              <div className="w-full bg-purple-200 rounded-full h-3">
                <div
                  className="bg-purple-600 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${scores.composition.score}%` }}
                />
              </div>
            </div>
            <div className="text-sm text-purple-900">
              {scores.composition.score >= 80 ? (
                <p>✅ Excellent composition! Keep up the great work.</p>
              ) : scores.composition.score >= 60 ? (
                <p>👍 Good composition. Room for improvement.</p>
              ) : scores.composition.score >= 40 ? (
                <p>⚠️ Fair composition. Focus on diet and exercise.</p>
              ) : (
                <p>
                  🚨 Needs attention. Consider consulting a health professional.
                </p>
              )}
            </div>
            <div className="mt-3 pt-3 border-t border-purple-200 text-xs text-purple-800">
              Based on: Fat {scores.composition.record.fat}%, Muscle{" "}
              {scores.composition.record.muscles}%, BMI{" "}
              {scores.composition.record.bmi}
            </div>
          </ScoreCard>

          {/* Waist-to-Hip Ratio */}
          <ScoreCard
            title="Waist-to-Hip Ratio"
            icon={TrendingUp}
            color={scores.whr.category.color}
          >
            <div className="mb-4">
              <div className="text-4xl font-bold text-gray-900 mb-2">
                {scores.whr.value}
              </div>
              <div
                className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                  scores.whr.category.color === "green"
                    ? "bg-green-200 text-green-800"
                    : scores.whr.category.color === "yellow"
                    ? "bg-yellow-200 text-yellow-800"
                    : "bg-red-200 text-red-800"
                }`}
              >
                {scores.whr.category.level} Risk
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-2">
              {scores.whr.category.message}
            </p>
            <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-600">
              Waist: {scores.whr.record.waistLine}" / Hip:{" "}
              {scores.whr.record.hip}"
            </div>
            {scores.whr.category.color !== "green" && (
              <div className="mt-3 p-3 bg-white rounded-lg">
                <p className="text-xs font-medium text-gray-700 mb-1">
                  💡 Recommendations:
                </p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Focus on core strengthening exercises</li>
                  <li>• Reduce refined carbohydrates</li>
                  <li>• Increase cardiovascular activity</li>
                </ul>
              </div>
            )}
          </ScoreCard>

          {/* Visceral Fat */}
          <ScoreCard
            title="Visceral Fat Level"
            icon={AlertCircle}
            color={scores.visceralFat.zone.color}
          >
            <div className="mb-4">
              <div className="text-4xl font-bold text-gray-900 mb-2">
                Level {scores.visceralFat.value}
              </div>
              <div
                className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                  scores.visceralFat.zone.color === "green"
                    ? "bg-green-200 text-green-800"
                    : scores.visceralFat.zone.color === "yellow"
                    ? "bg-yellow-200 text-yellow-800"
                    : "bg-red-200 text-red-800"
                }`}
              >
                {scores.visceralFat.zone.zone}
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-3">
              {scores.visceralFat.zone.message}
            </p>
            <div className="bg-white rounded-lg p-3">
              <p className="text-xs font-medium text-gray-700 mb-2">
                💡 Lifestyle Tips:
              </p>
              <ul className="text-xs text-gray-600 space-y-1">
                {scores.visceralFat.zone.tips.map((tip, idx) => (
                  <li key={idx}>• {tip}</li>
                ))}
              </ul>
            </div>
          </ScoreCard>

          {/* Info Box */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">
              📚 Understanding Your Scores
            </h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>
                • <strong>Composition Score:</strong> Overall body health
                combining multiple metrics
              </li>
              <li>
                • <strong>WHR:</strong> Central fat distribution indicator
              </li>
              <li>
                • <strong>Visceral Fat:</strong> Internal organ fat level
              </li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default HealthScores;
